import React, { useState, useEffect } from 'react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Input from '../../components/common/Input';
import Textarea from '../../components/common/Textarea';
import { providersAPI } from '../../api/providers';
import toast from 'react-hot-toast';
import { PlusIcon, PencilIcon, TagIcon } from '@heroicons/react/24/outline';

const ManageCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', icon: '' });
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const data = await providersAPI.getCategories();
      setCategories(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingCategory(null);
    setForm({ name: '', description: '', icon: '' });
    setIsActive(true);
    setErrors({});
    setShowModal(true);
  };

  const openEdit = (cat) => {
    setEditingCategory(cat);
    setForm({ name: cat.name, description: cat.description || '', icon: cat.icon || '' });
    setIsActive(cat.isActive !== false);
    setErrors({});
    setShowModal(true);
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (editingCategory) {
        await providersAPI.updateCategory(editingCategory.id, {
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          icon: form.icon.trim() || undefined,
          isActive,
        });
        toast.success('Category updated');
      } else {
        await providersAPI.createCategory({
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          icon: form.icon.trim() || undefined,
        });
        toast.success('Category created');
      }
      setShowModal(false);
      fetchCategories();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to save category';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">Manage Categories</h1>
            <p className="text-gray-600">Create and manage service categories for providers</p>
          </div>
          <Button onClick={openCreate} className="flex items-center gap-2">
            <PlusIcon className="w-5 h-5" />
            Add Category
          </Button>
        </div>

        <Card>
          {categories.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <TagIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No categories yet</p>
              <p className="text-sm mt-1">Add your first category to get started</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {categories.map((cat) => (
                <div
                  key={cat.id}
                  className="flex items-center gap-3 py-3 px-1 sm:px-2 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-gray-900 truncate" style={{ margin: 0, textAlign: 'left' }}>
                        {cat.name}
                      </p>
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold"
                        style={{
                          backgroundColor: cat.isActive !== false ? '#dcfce7' : 'var(--gray-3)',
                          color: cat.isActive !== false ? '#15803d' : 'var(--gray-10)',
                        }}
                      >
                        {cat.isActive !== false ? 'Active' : 'Inactive'}
                      </span>
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold"
                        style={{ backgroundColor: 'var(--orange-2)', color: 'var(--orange-11)' }}
                      >
                        {cat.serviceCount ?? 0} service{(cat.serviceCount ?? 0) !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 truncate mt-0.5" style={{ margin: 0, textAlign: 'left' }}>
                      {cat.description || 'No description'}
                    </p>
                  </div>

                  <button
                    onClick={() => openEdit(cat)}
                    className="inline-flex items-center gap-1.5 flex-shrink-0 text-sm font-medium px-3 py-1.5 rounded-lg transition-colors"
                    style={{ color: 'var(--orange-10)', border: '1px solid var(--orange-5)', backgroundColor: '#fff' }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--orange-2)')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#fff')}
                  >
                    <PencilIcon className="w-3.5 h-3.5" />
                    Edit
                  </button>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-5">
              {editingCategory ? 'Edit Category' : 'New Category'}
            </h2>

            <div className="space-y-4">
              <Input
                label="Category Name"
                name="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Hair & Beauty"
                error={errors.name}
                required
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <Textarea
                  name="description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Brief description of this category..."
                  rows={3}
                />
              </div>

              <Input
                label="Icon (emoji or text)"
                name="icon"
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
                placeholder="e.g., ✂️"
              />

              {editingCategory && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-800">Active</p>
                    <p className="text-xs text-gray-500">Inactive categories won't appear to providers</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="h-5 w-5 text-orange-500 rounded"
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} loading={saving}>
                {editingCategory ? 'Save Changes' : 'Create Category'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCategories;
